import { useEffect, useMemo, useState } from "react";
import { Box, Chip, CircularProgress, Tooltip, Typography } from "@mui/material";
import AddressbookPanelShell from "../../addressbook/panels/AddressbookPanelShell";
import ChatPanel from "../../addressbook/panels/ChatPanel";
import { apiGetCampaignContactLifecycles } from "../../../graphQL/campaign";

/*
 * CampaignAnalyticsPanel
 * ----------------------
 * Per-campaign results: the same numbers the batch analysis script reports
 * (sent / delivered / opened / clicked / bounced / unsubscribed) read from
 * campaignContactLifecycles, plus a chat that answers questions strictly from
 * those numbers (ChatPanel in context mode).
 *
 * Text (SMS) campaigns are not connected to a sending backend yet, so a text
 * campaign shows clearly-labelled sample numbers derived from its recipient
 * count.
 */

const LIFECYCLE_FETCH_LIMIT = 2000;

const INDIGO_900 = "#1A237E";
const INDIGO_500 = "#3F51B5";
const INK = "#1A1F36";
const MUTED = "#5A6182";
const TINT = "#F4F5FB";
const LINE = "#E8EAF6";

// One hue for the funnel (magnitude), status colors reserved for bad outcomes.
const STAGE_COLORS = {
  sent: "#283593",
  delivered: "#3F51B5",
  opened: "#5C6BC0",
  clicked: "#7986CB",
  replied: "#5C6BC0",
  bounced: "#C62828",
  failed: "#C62828",
  unsubscribed: "#8D6E63",
  optedOut: "#8D6E63",
};

const pct = (n, d) => (d > 0 ? Math.round((n / d) * 1000) / 10 : 0);

export const getCampaignChannel = (campaign) =>
  String(campaign?.data?.channel || "").toLowerCase() === "text" ? "text" : "email";

// Real numbers from lifecycle rows (dry-run rows excluded).
const summarizeEmail = (rows) => {
  const live = rows.filter((r) => !r?.dryRun && !String(r?.messageId || "").startsWith("dry-run:"));
  const isBounced = (r) =>
    Boolean(r.bouncedAt) || (r.suppressed && ["bounce", "complaint"].includes(r.suppressionReason));
  const isUnsub = (r) => r.suppressed && r.suppressionReason === "unsubscribe-link";
  const sent = live.filter((r) => ["sent"].includes(String(r.lifecycleState || "")) || r.deliveredAt || r.openedAt || r.clickedAt || isBounced(r));
  const delivered = sent.filter((r) => !isBounced(r) && (r.deliveredAt || r.openedAt || r.clickedAt));
  const opened = sent.filter((r) => !isBounced(r) && (r.openedAt || r.clickedAt));
  const clicked = sent.filter((r) => !isBounced(r) && r.clickedAt);
  const bounced = sent.filter(isBounced);
  const unsubscribed = live.filter(isUnsub);
  const failed = live.filter((r) => String(r.lifecycleState || "") === "failed");
  const queued = live.filter((r) => ["queued", "sending"].includes(String(r.lifecycleState || "")));
  return {
    channel: "email",
    sample: false,
    recipients: live.length,
    sent: sent.length,
    delivered: delivered.length,
    opened: opened.length,
    clicked: clicked.length,
    bounced: bounced.length,
    unsubscribed: unsubscribed.length,
    failed: failed.length,
    queued: queued.length,
  };
};

// Sample numbers for a text campaign (no SMS backend yet). Fixed 500-contact
// base (a typical purchased list) so the demo reads the same every time.
const summarizeTextSample = () => {
  const n = 500;
  const delivered = Math.round(n * 0.96);
  return {
    channel: "text",
    sample: true,
    recipients: n,
    sent: n,
    delivered,
    replied: Math.round(n * 0.07),
    clicked: Math.round(n * 0.04),
    optedOut: Math.round(n * 0.02),
    failed: n - delivered,
  };
};

const buildStages = (s) => {
  if (s.channel === "text") {
    return [
      { key: "sent", label: "Sent", value: s.sent, base: s.recipients },
      { key: "delivered", label: "Delivered", value: s.delivered, base: s.sent },
      { key: "replied", label: "Replied", value: s.replied, base: s.delivered },
      { key: "clicked", label: "Clicked link", value: s.clicked, base: s.delivered },
      { key: "optedOut", label: "Opted out", value: s.optedOut, base: s.delivered },
      { key: "failed", label: "Failed", value: s.failed, base: s.sent },
    ];
  }
  return [
    { key: "sent", label: "Sent", value: s.sent, base: s.recipients },
    { key: "delivered", label: "Delivered", value: s.delivered, base: s.sent },
    { key: "opened", label: "Opened", value: s.opened, base: s.delivered },
    { key: "clicked", label: "Clicked", value: s.clicked, base: s.delivered },
    { key: "bounced", label: "Bounced", value: s.bounced, base: s.sent },
    { key: "unsubscribed", label: "Unsubscribed", value: s.unsubscribed, base: s.delivered },
  ];
};

// Plain-text block the chat answers from.
const buildContext = (campaign, s, stages) => {
  const lines = [
    "CAMPAIGN RESULTS",
    `Campaign: ${campaign?.name || "Untitled"}`,
    `Channel: ${s.channel === "text" ? "text message (SMS)" : "email"}`,
    `Status: ${campaign?.status || ""}`,
    s.sample ? "Note: these are sample numbers; text sending is not connected yet." : "",
    `Recipients: ${s.recipients}`,
    "",
    "COUNTS",
    ...stages.map((st) => `${st.label}: ${st.value}`),
    "",
    "RATES (use these exact names)",
    ...(s.channel === "text"
      ? [
          `Delivery rate: ${pct(s.delivered, s.sent)}% (delivered ${s.delivered} of ${s.sent} sent)`,
          `Reply rate: ${pct(s.replied, s.delivered)}% (replied ${s.replied} of ${s.delivered} delivered)`,
          `Click rate: ${pct(s.clicked, s.delivered)}% (clicked ${s.clicked} of ${s.delivered} delivered)`,
          `Opt-out rate: ${pct(s.optedOut, s.delivered)}% (opted out ${s.optedOut} of ${s.delivered} delivered)`,
          `Failure rate: ${pct(s.failed, s.sent)}% (failed ${s.failed} of ${s.sent} sent)`,
        ]
      : [
          `Delivery rate: ${pct(s.delivered, s.sent)}% (delivered ${s.delivered} of ${s.sent} sent)`,
          `Open rate: ${pct(s.opened, s.delivered)}% (opened ${s.opened} of ${s.delivered} delivered)`,
          `Click rate: ${pct(s.clicked, s.delivered)}% (clicked ${s.clicked} of ${s.delivered} delivered)`,
          `Bounce rate: ${pct(s.bounced, s.sent)}% (bounced ${s.bounced} of ${s.sent} sent)`,
          `Unsubscribe rate: ${pct(s.unsubscribed, s.delivered)}% (unsubscribed ${s.unsubscribed} of ${s.delivered} delivered)`,
        ]),
  ];
  if (s.channel === "email") {
    lines.push(
      "Note: clicks can include automated email-security scanners that open every link at delivery; treat click counts as an upper bound.",
    );
  }
  return lines.filter(Boolean).join("\n");
};

const StatTile = ({ label, value, sub }) => (
  <Box sx={{ flex: "1 1 96px", minWidth: 96, p: 1, borderRadius: 1.5, backgroundColor: TINT, border: `1px solid ${LINE}` }}>
    <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: 0.4 }}>{label}</Typography>
    <Typography sx={{ fontSize: 22, fontWeight: 700, color: INDIGO_900, lineHeight: 1.2 }}>{value}</Typography>
    {sub ? <Typography sx={{ fontSize: 11, color: MUTED }}>{sub}</Typography> : null}
  </Box>
);

const StageBar = ({ stage, max }) => {
  const width = max > 0 ? Math.max(2, Math.round((stage.value / max) * 100)) : 2;
  const rate = pct(stage.value, stage.base);
  return (
    <Tooltip title={`${stage.label}: ${stage.value} of ${stage.base} (${rate}%)`} placement="top" arrow>
      <Box sx={{ display: "grid", gridTemplateColumns: "96px 1fr 88px", alignItems: "center", gap: 1, py: 0.35 }}>
        <Typography sx={{ fontSize: 12, color: INK, fontWeight: 600 }}>{stage.label}</Typography>
        <Box sx={{ height: 14, backgroundColor: TINT, borderRadius: 1, overflow: "hidden" }}>
          <Box sx={{ width: `${width}%`, height: "100%", backgroundColor: STAGE_COLORS[stage.key] || INDIGO_500, borderRadius: 1 }} />
        </Box>
        <Typography sx={{ fontSize: 12, color: INK, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
          {stage.value} <Box component="span" sx={{ color: MUTED }}>({rate}%)</Box>
        </Typography>
      </Box>
    </Tooltip>
  );
};

const CampaignAnalyticsPanel = ({ campaign, authorId, lifecycleRefreshKey, onClose }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const campaignId = campaign?.id && !campaign?._isNew ? campaign.id : "";
  const domain = String(campaign?.domain || "").trim();
  const channel = getCampaignChannel(campaign);

  useEffect(() => {
    if (!campaignId || !domain) {
      setRows([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const result = await apiGetCampaignContactLifecycles({
          campaignId,
          domain,
          pagination: { page: 1, limit: LIFECYCLE_FETCH_LIMIT },
        });
        if (!cancelled) setRows(Array.isArray(result?.items) ? result.items : []);
      } catch (err) {
        console.error("Failed to load campaign analytics:", err);
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [campaignId, domain, lifecycleRefreshKey]);

  const summary = useMemo(
    () => (channel === "text" ? summarizeTextSample() : summarizeEmail(rows)),
    [channel, rows],
  );
  const stages = useMemo(() => buildStages(summary), [summary]);
  const contextText = useMemo(() => buildContext(campaign, summary, stages), [campaign, summary, stages]);
  const max = Math.max(summary.sent, 1);

  const headline = channel === "text"
    ? [
        { label: "Sent", value: summary.sent },
        { label: "Delivered", value: `${pct(summary.delivered, summary.sent)}%`, sub: `${summary.delivered}` },
        { label: "Replied", value: `${pct(summary.replied, summary.delivered)}%`, sub: `${summary.replied}` },
        { label: "Opted out", value: `${pct(summary.optedOut, summary.delivered)}%`, sub: `${summary.optedOut}` },
      ]
    : [
        { label: "Sent", value: summary.sent },
        { label: "Delivered", value: `${pct(summary.delivered, summary.sent)}%`, sub: `${summary.delivered}` },
        { label: "Opened", value: `${pct(summary.opened, summary.delivered)}%`, sub: `${summary.opened}` },
        { label: "Clicked", value: `${pct(summary.clicked, summary.delivered)}%`, sub: `${summary.clicked}` },
      ];

  return (
    <AddressbookPanelShell
      onClose={onClose}
      collectionLabel="Campaign Analytics"
      titleContent={(
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: "text.primary", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {campaign?.name ? `Analytics: ${campaign.name}` : "Analytics"}
          </Typography>
          {summary.sample ? (
            <Chip size="small" label="Sample data" sx={{ backgroundColor: "#FFF8E1", color: "#8D6E63", fontWeight: 700, fontSize: 10.5 }} />
          ) : null}
        </Box>
      )}
    >
      {!campaignId ? (
        <Typography sx={{ textAlign: "center", py: 4, fontSize: 13, color: "text.secondary" }}>
          Select a campaign to see its results.
        </Typography>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, height: "100%", minHeight: 0 }}>
          {loading ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: MUTED, fontSize: 12 }}>
              <CircularProgress size={14} sx={{ color: INDIGO_500 }} /> Loading results
            </Box>
          ) : null}

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {headline.map((t) => (
              <StatTile key={t.label} label={t.label} value={t.value} sub={t.sub} />
            ))}
          </Box>

          <Box sx={{ p: 1.25, borderRadius: 1.5, border: `1px solid ${LINE}`, backgroundColor: "#FFFFFF" }}>
            {stages.map((st) => (
              <StageBar key={st.key} stage={st} max={max} />
            ))}
          </Box>

          <Box sx={{ flex: 1, minHeight: 320, border: `1px solid ${LINE}`, borderRadius: 1.5, overflow: "hidden" }}>
            <ChatPanel
              key={`${campaignId}-${channel}`}
              authorId={authorId}
              domain={domain}
              contextText={contextText}
              title="Ask about these results"
              emptyTitle="Ask about this campaign"
              emptySubtitle="Try: who is worth calling first, or what the open rate is"
              inputPlaceholder="Ask about the results..."
              maxTokens={350}
            />
          </Box>
        </Box>
      )}
    </AddressbookPanelShell>
  );
};

export default CampaignAnalyticsPanel;
